import { useMemo } from 'react'
import cc from 'classcat'

import type { ItemType } from '../LinkTooltip.utils'
import { SET_CATEGORY_STATE, SET_LINK_DETAILS, dropdownCategoryInitialState } from '../LinkTooltip.constants'
import { sortItemsToStartWithHomeType } from '../LinkTooltip.utils'
import { useTooltipState } from '../LinkTooltipContext'
import { useTranslate } from '../../../../../../../utils/translate'

function SearchResultsDropdown({
  inputRef,
  categories,
  onItemClick,
  searchTerm,
  isSettingsLayer,
}: SearchResultsDropdownProps) {
  const { categoryState, linkTooltipDispatch, categoryDispatch } = useTooltipState()

  const handleCategoryClick = (categoryName: string) => {
    categoryDispatch({
      type: SET_CATEGORY_STATE,
      payload: {
        selectedCategory: categoryName,
        searchCategory: categoryName,
        showCategories: false,
      },
    })
    inputRef?.current?.focus()
  }

  const handleBackClick = () => {
    categoryDispatch({ type: SET_CATEGORY_STATE, payload: dropdownCategoryInitialState })
    linkTooltipDispatch({
      type: SET_LINK_DETAILS,
      payload: {
        searchTerm: '',
      },
    })
  }

  const handleItemClick = (item: ItemType) => {
    onItemClick(item)
  }

  const searchResults = useMemo(() => {
    return searchTerm
      ? categories
          .filter((category) => !categoryState.searchCategory || category.name === categoryState.searchCategory)
          .reduce<ItemType[]>((acc, category) => acc.concat(category.items), [])
      : []
  }, [categories, searchTerm, categoryState.searchCategory])

  if (categoryState.showCategories && !searchTerm) {
    return (
      <div
        className={cc(['dali-plugin-link-tooltip-search-results-dropdown', { top: isSettingsLayer }])}
        data-testid="search-form-suggestions"
      >
        {categories.map((category) => (
          <LinkTooltipCategory
            key={category.name}
            name={category.name}
            onClick={() => handleCategoryClick(category.name)}
          />
        ))}
      </div>
    )
  }

  if (!categoryState.showCategories && !searchTerm && categoryState.selectedCategory) {
    const selectedCategoryItems =
      categories.find((category) => category.name === categoryState.selectedCategory)?.items || []
    const sortedItems = sortItemsToStartWithHomeType(selectedCategoryItems)

    return (
      <div
        className={cc(['dali-plugin-link-tooltip-search-results-dropdown', { top: isSettingsLayer }])}
        data-testid="search-form-suggestions"
      >
        <LinkTooltipCategory name={categoryState.selectedCategory} onClick={handleBackClick} isBackButton={true} />
        <div className="scrollable-container">
          {sortedItems.map((item) => (
            <LinkTooltipItem
              key={item.id}
              item={item}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleItemClick(item)
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  if (searchTerm) {
    return (
      <div
        className={cc(['dali-plugin-link-tooltip-search-results-dropdown', { top: isSettingsLayer }])}
        data-testid="search-form-suggestions"
      >
        {categoryState.selectedCategory && searchResults.length > 0 && (
          <LinkTooltipCategory name={categoryState.selectedCategory} onClick={handleBackClick} isBackButton={true} />
        )}
        <div className="scrollable-container">
          {searchResults.map((item) => (
            <LinkTooltipItem
              key={item.id}
              item={item}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleItemClick(item)
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  return null
}

export default SearchResultsDropdown

function LinkTooltipCategory({ name, onClick, isBackButton }: Readonly<CategoryProps>) {
  const t = useTranslate('shop', 'components.richTextComponent.linkInputField.dropdownCategory')

  const lowerCaseName = name.toLowerCase()

  const className = `dali-plugin-link-tooltip-item ${
    isBackButton
      ? 'dali-plugin-link-tooltip-content-type-category-border'
      : `dali-plugin-link-tooltip-content-type-category-${lowerCaseName}`
  }`
  return (
    <button
      className={className}
      data-testid="search-form-suggestions-category"
      onClick={onClick}
      aria-label={t(isBackButton ? 'back' : lowerCaseName)}
    >
      <span
        className={
          isBackButton
            ? 'dali-plugin-link-tooltip-category-left-arrow'
            : 'dali-plugin-link-tooltip-category-right-arrow'
        }
      >
        {t(lowerCaseName)}
      </span>
    </button>
  )
}

export function LinkTooltipItem({ item, onClick }: Readonly<ItemProps>) {
  return (
    <button
      title={item.link}
      className={`dali-plugin-link-tooltip-item dali-plugin-link-tooltip-content-type-${item.type}`}
      data-testid="search-form-suggestions-item"
      onClick={onClick}
      aria-label={item.title}
    >
      <span className="dali-plugin-link-tooltip-item-text">{item.title}</span>
    </button>
  )
}

export interface Category {
  name: string
  items: ItemType[]
}

interface SearchResultsDropdownProps {
  readonly inputRef?: React.RefObject<HTMLInputElement | null>
  readonly categories: Category[]
  readonly onItemClick: (item: ItemType) => void
  readonly searchTerm: string
  readonly isSettingsLayer?: boolean
}

interface CategoryProps {
  name: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  isBackButton?: boolean
}

interface ItemProps {
  item: ItemType
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}
